import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { LightBulbIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { memoryAPI } from '../../services/api'

/**
 * VH-235: "Remember this?" — a fact the chat noticed, offered with the exact text it would store.
 * Save writes it (POST /memories, source chat); Dismiss stores nothing. A failed save keeps the
 * card on screen with a retry, never a half-saved entry.
 */
export function MemoryProposalCard({ proposal }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [state, setState] = useState('open') // open | saving | saved | dismissed | failed

  if (state === 'dismissed') return null

  const save = async () => {
    setState('saving')
    try {
      if (proposal.replaces_id) {
        await memoryAPI.update(proposal.replaces_id, { text: proposal.text, category: proposal.category, language: 'en' })
      } else {
        await memoryAPI.create({
          text: proposal.text, category: proposal.category, language: 'en',
          source: 'chat', source_message_id: proposal.source_message_id,
        })
      }
      queryClient.invalidateQueries({ queryKey: ['memories'] })
      setState('saved')
    } catch {
      setState('failed')
    }
  }

  return (
    <div className="ml-2 rounded-lg border border-teal-300 bg-teal-50 p-3 text-sm space-y-2" data-testid="memory-proposal">
      <p className="flex items-start gap-2 text-teal-900">
        <LightBulbIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span>
          <span className="font-medium">{proposal.replaces_id ? t('memory.proposal.update') : t('memory.proposal.title')}</span>
          {' '}<span className="text-gray-900">“{proposal.text}”</span>
          <span className="block text-xs text-gray-600">{t(`memory.categories.${proposal.category}`)}</span>
        </span>
      </p>
      {state === 'saved' ? (
        <p className="text-green-800" role="status" data-testid="memory-proposal-saved">
          {t('memory.proposal.saved')}{' '}
          <Link to="/profile?tab=memory" className="underline">{t('memory.proposal.seeAll')}</Link>
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-primary text-sm" disabled={state === 'saving'} onClick={save}
            data-testid="memory-proposal-save">{state === 'failed' ? t('memory.proposal.retry') : t('memory.proposal.save')}</button>
          <button type="button" className="btn-outline text-sm" disabled={state === 'saving'}
            onClick={() => setState('dismissed')} data-testid="memory-proposal-dismiss">{t('memory.proposal.dismiss')}</button>
          {state === 'failed' && <span className="text-red-700" role="alert">{t('memory.failed')}</span>}
        </div>
      )}
    </div>
  )
}

export default MemoryProposalCard
